import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Table, Tag, Select, Input, DatePicker, Typography, Badge, Button, Tooltip } from 'antd'
import { SearchOutlined, FileTextOutlined, CommentOutlined, MailOutlined, PlayCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { usePracticeAgentHistory } from '../hooks'
import type { PracticeAgentActivity, PracticeAgentActivityType, PracticeAgentDeepDiveDetails, PracticeAgentHistoryFilters } from '../types'
import PageLoading from '../../../components/PageLoading'
import FeatureThemeProvider from '../../../components/FeatureThemeProvider'
import { EmptyState } from '../workspace/components'
import { useAuthSession } from '../../../lib/session'
import { getFeatureThemeClassName, getFeatureThemeStyle, productThemes } from '../../../lib/productBranding'

const { RangePicker } = DatePicker
const { Title } = Typography

const typeConfig: Record<PracticeAgentActivityType, { label: string; color: string; icon: React.ReactNode }> = {
  ReportViewed: { label: 'Report Viewed', color: 'blue', icon: <FileTextOutlined /> },
  DeepDive: { label: 'Deep Dive', color: 'purple', icon: <CommentOutlined /> },
  CommunicationSent: { label: 'Communication Sent', color: 'green', icon: <MailOutlined /> },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getActivitySummary(activity: PracticeAgentActivity): React.ReactNode {
  switch (activity.type) {
    case 'ReportViewed': {
      const viewedBy = activity.insertUserName ?? activity.insertUserId ?? 'Unknown user'
      const target = activity.reportTitle ?? activity.groupName ?? 'Unknown report/group'
      return `${viewedBy} viewed ${target}`
    }
    case 'DeepDive': {
      const d = activity.details as PracticeAgentDeepDiveDetails | undefined
      let filterLine: string | null = null
      let dimensionLine: string | null = null
      if (d?.drilldownQuery?.filters) {
        const entries = Object.entries(d.drilldownQuery.filters)
        if (entries.length) filterLine = `Filtered by: ${entries.map(([k, v]) => `${k}: ${v}`).join(', ')}`
      }
      if (d?.drilldownQuery?.dimension) dimensionLine = `Breakdown by: ${d.drilldownQuery.dimension}`
      if (!filterLine && !dimensionLine) return 'AI deep-dive investigation'
      return (
        <span className="flex flex-col gap-0.5">
          {filterLine && <span>{filterLine}</span>}
          {dimensionLine && <span className="text-xs text-gray-400">{dimensionLine}</span>}
        </span>
      )
    }
    case 'CommunicationSent': {
      const d = activity.details as { recipients?: string[]; subject?: string; type?: string } | undefined
      const recipCount = d?.recipients?.length ?? 0
      return `"${d?.subject ?? 'No subject'}" → ${recipCount} recipient${recipCount !== 1 ? 's' : ''}`
    }
  }
}

const PAGE_SIZE = 10

export default function HistoryPage() {
  const session = useAuthSession()
  const featureThemeStyle = getFeatureThemeStyle(productThemes.pracIntel)
  const clientId = session?.currentClientId ?? ''
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const scopedReportBuilderId = searchParams.get('reportBuilderId') ?? undefined
  const scopedReportBuilderGroupId = searchParams.get('reportBuilderGroupId') ?? undefined
  const [filters, setFilters] = useState<PracticeAgentHistoryFilters>({ page: 1, pageSize: PAGE_SIZE })
  const [reportSearch, setReportSearch] = useState('')
  const [debouncedReportSearch, setDebouncedReportSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState<[string, string] | null>(null)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedReportSearch(reportSearch)
    }, 250)

    return () => window.clearTimeout(timeoutId)
  }, [reportSearch])

  const appliedFilters: PracticeAgentHistoryFilters = {
    ...filters,
    type: typeFilter === 'all' ? undefined : (typeFilter as PracticeAgentActivityType),
    reportTitle: debouncedReportSearch || undefined,
    fromDate: dateRange?.[0],
    toDate: dateRange?.[1],
    reportBuilderId: scopedReportBuilderId,
    reportBuilderGroupId: scopedReportBuilderGroupId,
  }

  const { data, isLoading, isError } = usePracticeAgentHistory(clientId, appliedFilters)

  function handleReopenDeepDive(activity: PracticeAgentActivity) {
    if (activity.type !== 'DeepDive') return
    const targetPath = activity.reportBuilderGroupId
      ? `/reports/workspace/group/${activity.reportBuilderGroupId}`
      : activity.reportBuilderId
        ? `/reports/workspace/${activity.reportBuilderId}`
        : null
    if (!targetPath) return

    const details = activity.details as PracticeAgentDeepDiveDetails | undefined

    const params = new URLSearchParams()
    params.set('deepDiveVersion', activity.practiceAgentActivityId)
    const drillFilters = details?.drilldownQuery?.filters
    const dimension = details?.drilldownQuery?.dimension
    if (drillFilters && Object.keys(drillFilters).length > 0) {
      params.set('drilldownFilters', JSON.stringify(drillFilters))
    }
    if (dimension) {
      params.set('drilldownDimension', dimension)
    }
    if (activity.reportBuilderGroupId && activity.reportBuilderId) {
      params.set('drilldownReportBuilderId', activity.reportBuilderId)
    }

    navigate(`${targetPath}?${params.toString()}`)
  }

  const columns: ColumnsType<PracticeAgentActivity> = [
    {
      title: 'Date',
      dataIndex: 'insertDate',
      key: 'insertDate',
      width: 170,
      render: (v: string) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span>
      ),
      sorter: (a, b) => a.insertDate.localeCompare(b.insertDate),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 170,
      render: (type: PracticeAgentActivityType) => {
        const cfg = typeConfig[type]
        return (
          <Tag color={cfg.color} icon={cfg.icon}>
            {cfg.label}
          </Tag>
        )
      },
    },
    {
      title: 'Report / Group',
      key: 'report',
      render: (_: unknown, record: PracticeAgentActivity) => (
        <span className="text-sm font-medium text-gray-800">
          {record.reportTitle ?? record.groupName ?? '—'}
        </span>
      ),
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_: unknown, record: PracticeAgentActivity) => (
        <span className="text-sm text-gray-600">{getActivitySummary(record)}</span>
      ),
    },
    {
      title: 'User',
      dataIndex: 'insertUserName',
      key: 'user',
      width: 150,
      render: (name: string) => (
        <span className="text-sm text-gray-600">{name ?? '—'}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_: unknown, record: PracticeAgentActivity) => {
        if (record.type !== 'DeepDive' || (!record.reportBuilderId && !record.reportBuilderGroupId)) return null;
        else if(record.type === 'DeepDive') {
          const details = record.details as PracticeAgentDeepDiveDetails | undefined
          if(!details?.drilldownQuery?.filters) return null;
          return (
            <Tooltip title={`Re-open ${record.reportTitle ?? record.groupName ?? 'deep dive'} in workspace`}>
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleReopenDeepDive(record)}
                aria-label={`Re-open ${record.reportTitle ?? record.groupName ?? 'deep dive'} in workspace`}
              >
                Re-open
              </Button>
            </Tooltip>
          )
        }
      },
    },
  ]

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd>
    <div className={`${getFeatureThemeClassName('pracIntel')} max-w-6xl mx-auto space-y-6`} style={featureThemeStyle}>
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button
          icon={<ArrowLeftOutlined />}
          type="text"
          onClick={() => {
            if (scopedReportBuilderGroupId) navigate(`/reports/workspace/group/${scopedReportBuilderGroupId}`)
            else if (scopedReportBuilderId) navigate(`/reports/workspace/${scopedReportBuilderId}`)
            else navigate(-1)
          }}
          aria-label="Back"
        />
        <div>
          <Title level={4} className="!mb-1">PracIntel History</Title>
          <p className="text-sm text-gray-500">Audit trail of all investigations, communications, and analysis.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-4 shadow-card">
        <Input
          placeholder="Search by report name…"
          prefix={<SearchOutlined className="text-gray-400" />}
          value={reportSearch}
          onChange={(e) => {
            setReportSearch(e.target.value)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
          className="w-56"
          allowClear
        />
        <Select
          value={typeFilter}
          onChange={(v) => {
            setTypeFilter(v)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
          className="w-48"
          options={[
            { value: 'all', label: 'All types' },
            { value: 'ReportViewed', label: 'Report Viewed' },
            { value: 'DeepDive', label: 'Deep Dive' },
            { value: 'CommunicationSent', label: 'Communication Sent' },
          ]}
        />
        <RangePicker
          onChange={(_, strs) => {
            const [from, to] = strs as [string, string]
            setDateRange(from && to ? [from, to] : null)
            setFilters((f) => ({ ...f, page: 1 }))
          }}
          className="w-64"
        />
        {data && (
          <div className="ml-auto flex items-center">
            <Badge count={data.total} overflowCount={999} color={productThemes.pracIntel.primary} />
            <span className="ml-2 text-sm text-gray-500">activities</span>
          </div>
        )}
      </div>

      {/* Table */}
      <PageLoading show={isLoading} theme={productThemes.pracIntel} />
      <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
        {!isLoading && (isError ? (
          <EmptyState title="Failed to load history" description="An error occurred fetching activity data." />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No activity found"
            description="No history matches your current filters."
          />
        ) : (
          <Table<PracticeAgentActivity>
            rowKey="practiceAgentActivityId"
            columns={columns}
            dataSource={data.items}
            pagination={{
              current: data.page,
              pageSize: data.pageSize,
              total: data.total,
              showSizeChanger: false,
              onChange: (page) => setFilters((f) => ({ ...f, page })),
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
            }}
            size="middle"
          />
        ))}
      </div>
    </div>
    </FeatureThemeProvider>
  )
}
